import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription } from 'rxjs';

interface RuntimeValidator {
    fn: (form: FormGroup, options?: any) => ValidatorFn;
    options?: any;
}

export interface CmpsFormField {
    id?: string;
    name: string;
    labelId?: string;
    label: string;
    placeholder?: string;
    validators?: any[];
    runtimeValidators?: RuntimeValidator[];
    validatorDirectives?: any[];
    validationMsgs?: { [key: string]: any };
    type?: 'control' | 'array';
}

export interface CmpsFormFields<F extends CmpsFormField = CmpsFormField> {
    [key: string]: F;
}

interface CmpsFormValidationErrors extends ValidationErrors {
    _controls?: ValidationErrors;
}

export function rollupErrors(
    control: FormGroup | FormArray,
): CmpsFormValidationErrors | null {
    const errors: CmpsFormValidationErrors = {};
    Object.keys(control.controls).forEach((c) => {
        const ctrl = control.controls[c];
        if (!ctrl.errors) {
            return;
        }
        errors[c] = ctrl.errors;
    });
    if (Object.keys(errors).length !== 0) {
        return { ...(control.errors || {}), _controls: errors };
    }
    return control.errors;
}

export class CmpsForm<F extends CmpsFormFields = CmpsFormFields, V = any> {
    private readonly _fieldKeys: string[];
    readonly warnings: any;
    readonly errors: any;
    private readonly _sub: Subscription;

    get value(): V {
        return !this.form ? null : this.form.value;
    }

    constructor(
        private readonly _translateService: TranslateService,
        public readonly form: FormGroup,
        public readonly fields: F,
        public readonly name: string,
    ) {
        this._fieldKeys = Object.keys(this.fields);
        this._sub = this.form.statusChanges.subscribe(() =>
            this._setWarningsErrors(),
        );

        this.warnings = {};
        this.errors = {};
        this.clearWarningsErrors();
    }

    protected _renderMsg(
        field: CmpsFormField,
        msg: string,
        errors: any,
    ): string {
        if (!msg) return '';
        if (msg.length === 0) return msg;
        const fieldLabel =
            !field.label || field.label.length === 0
                ? ''
                : this._translateService.instant(field.label);
        msg = this._translateService.instant(msg);
        msg = msg.replace(/{{field_label}}/g, fieldLabel);
        if (typeof errors === 'object') {
            for (const key in errors) {
                let errorMsg = errors[key];
                if (!errorMsg) continue;
                if (typeof errorMsg !== 'string') {
                    try {
                        errorMsg = errorMsg.toString();
                    } catch (err) {
                        console.log("can't cast to string");
                        continue;
                    }
                }
                if (errorMsg.length === 0) continue;
                errorMsg = this._translateService.instant(errorMsg);
                msg = msg.replace(new RegExp(`{{${key}}}`, 'g'), errorMsg);
            }
        }
        return msg;
    }

    protected _mapMsgs(
        field: CmpsFormField,
        fieldKey: string,
        dest: any,
        src: any,
    ): void {
        for (const key in src) {
            const msg = !field.validationMsgs
                ? null
                : field.validationMsgs[key];
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            dest[fieldKey] += `${this._renderMsg(field, msg, src[key])} `;
        }
        dest[fieldKey] = dest[fieldKey].trim();
    }

    protected _setWarnings(fieldKey: string): void {
        this.warnings[fieldKey] = '';
        const field = this.fields[fieldKey];
        if (!field.validatorDirectives) return;
        const control = this.form.get(field.name);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (control && control.valid) {
            const warnings = field.validatorDirectives
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-prototype-builtins
                .filter(
                    (value) =>
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        value &&
                        // eslint-disable-next-line no-prototype-builtins
                        value.hasOwnProperty('warnings') &&
                        value.warnings != null,
                )
                .map((value) => value.warnings)
                .reduce((a, b) => ({ ...a, ...b }), {});
            this._mapMsgs(field, fieldKey, this.warnings, warnings);
        }
    }

    protected _setErrors(fieldKey: string): void {
        this.errors[fieldKey] = '';
        const field = this.fields[fieldKey];
        const control = this.form.get(field.name);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (control && control.dirty && !control.valid) {
            this._mapMsgs(field, fieldKey, this.errors, control.errors);
        }
    }

    protected _setWarningsErrors(): void {
        if (!this.form) return;
        this._fieldKeys.forEach((key) => {
            this._setWarnings(key);
            this._setErrors(key);
        });
    }

    setValidators(
        field: CmpsFormField,
        validators?: ValidatorFn[],
        runtimeValidators?: RuntimeValidator[],
    ): void {
        let next = [];
        if (field.validators) {
            next = next.concat(field.validators);
        }
        if (field.runtimeValidators) {
            next = next.concat(
                field.runtimeValidators.map((v) => v.fn(this.form, v.options)),
            );
        }
        if (validators) {
            next = next.concat(validators);
        }
        if (runtimeValidators) {
            next = next.concat(
                runtimeValidators.map((v) => v.fn(this.form, v.options)),
            );
        }
        const control = this.form.get(field.name);
        if (control) {
            control.setValidators(next);
        }
    }

    rollupErrors(): CmpsFormValidationErrors | null {
        return rollupErrors(this.form);
    }

    checkWarningsErrors(): void {
        Object.keys(this.form.controls).forEach((c) =>
            this.form.controls[c].markAsDirty(),
        );
        this._setWarningsErrors();
    }

    /** for use with CmpsFormService.markAsDirty() */
    checkWarningsErrors2(): void {
        this._setWarningsErrors();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    clearWarningsErrors() {
        this._fieldKeys.forEach((key) => {
            this.warnings[key] = '';
            this.errors[key] = '';
        });
    }

    reset(
        value?: any,
        options?: { onlySelf?: boolean; emitEvent?: boolean },
    ): void {
        this.clearWarningsErrors();

        if (this.form) {
            this.form.reset(value, options);
        }
    }

    destroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
    }
}

@Injectable()
export class CmpsFormBuilder {
    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _translateService: TranslateService,
    ) {}

    create<F extends CmpsFormFields = CmpsFormFields>(
        fields: F,
        opts: { name?: string; value?: any } = {},
    ): CmpsForm<F> {
        const form = this._formBuilder.group({});
        const keys = Object.keys(fields);
        keys.forEach((key) => {
            const field = fields[key];
            let validators = [];
            if (field.validators) {
                validators = validators.concat(field.validators);
            }
            if (field.runtimeValidators) {
                validators = validators.concat(
                    field.runtimeValidators.map((v) => v.fn(form, v.options)),
                );
            }
            let ctrl;
            switch (field.type) {
                case 'array':
                    ctrl = this._formBuilder.array([], validators);
                    break;
                default:
                    ctrl = this._formBuilder.control(opts.value, validators);
            }
            form.addControl(field.name, ctrl);
        });
        return new CmpsForm(
            this._translateService,
            form,
            fields,
            opts.name || `form${Math.random()}`,
        );
    }
}

function findElement(
    name: string,
    fieldId: string,
    fieldName: string,
): HTMLElement {
    let el;
    if (fieldId) {
        el = document.querySelector(`form[name="${name}"] [id="${fieldId}"]`);
    }
    if (fieldName) {
        el = document.querySelector(
            `form[name="${name}"] [name="${fieldName}"]`,
        );
    }
    return el;
}

export function getElement<F extends CmpsFormFields = CmpsFormFields>(
    form: CmpsForm<F> | string,
    field: CmpsFormField | string,
): HTMLElement {
    const name = typeof form === 'string' ? form : form.name;
    let el;
    if (typeof field === 'string') {
        el = findElement(name, null, field);
    } else {
        if (field.id) {
            el = findElement(name, field.id, null);
        }
        if (!el) {
            el = findElement(name, null, field.name);
        }
    }
    if (!el) {
        console.error('element not found', form, field);
    }
    return el;
}

export interface CmpsFormConnector {
    /** should be an @Input() */
    cmpsFormControlPath: string[];

    /** should be an @Input(). piggyback angular.
     * either this or formControlName should be specified */
    formControl?: FormControl;

    /** should be an @Input(). piggyback angular.
     either this or formControl should be specified */
    formControlName?: string;
}

/** intended for template-tree-linking forms isolated across components/modules.
 * this should be provided at the component level of the most common ancestor.
 * multiple separate tree-linked forms under the same ancestor are not supported. */
export class CmpsFormService {
    private readonly _controls: { [formControlName: string]: AbstractControl };
    private readonly _registerCbs: { [formControlName: string]: Subject<any> };

    constructor() {
        this._controls = {};
        this._registerCbs = {};
    }

    register(path: string[], control: AbstractControl): void {
        const key = path.join('.');
        this._controls[key] = control;

        Object.entries(this._registerCbs).forEach(([k, v]) => {
            const index = k.indexOf('*');
            if (
                (index !== -1 && key.startsWith(k.substring(0, index))) ||
                k === key
            ) {
                v?.next(control);
            }
        });
    }

    unregister(path: string[]): void {
        const key = path.join('.');
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this._controls[key];
    }

    onRegister<T extends AbstractControl>(path: string[]): Observable<T> {
        const subj = new Subject<T>();
        this._registerCbs[path.join('.')] = subj;
        return subj.asObservable();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deleteOnRegister<T extends AbstractControl>(path: string[]): void {
        const key = path.join('.');
        if (this._registerCbs?.[key] != null) {
            this._registerCbs[key]?.complete();
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this._registerCbs[key];
        }
    }

    get<T extends AbstractControl>(path: string[]): T {
        return this._controls?.[path.join('.')] as T;
    }

    markAsDirty(): void {
        Object.values(this._controls).forEach((e) => e.markAsDirty());
    }
}

export function cmpsFormServiceFactory(): CmpsFormService {
    return new CmpsFormService();
}
